import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Crown, Palette, Heart } from 'lucide-react';
import styled from 'styled-components';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background-color: ${props => props.theme.colors.background.main};
  position: relative;
  overflow: hidden;
  id: why-blue;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const DecorativeBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background: linear-gradient(to left, ${props => props.theme.colors.primary.main}0D, transparent);
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  position: relative;
  z-index: 10;
`;

const ContentWrapper = styled.div`
  max-width: 75rem;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Subtitle = styled.span`
  color: ${props => props.theme.colors.accent.gold};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.fontSize.lg};
  max-width: 42rem;
  margin: 0 auto;
  line-height: 1.7;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
    max-width: 48rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    max-width: 56rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    max-width: 64rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  background-color: ${props => props.theme.colors.background.card};
  border: 1px solid ${props => props.theme.colors.border.main};
  transition: ${props => props.theme.transitions.slow};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary.main}4D;
    box-shadow: ${props => props.theme.shadows.elegant};
  }
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.main}1A;
  color: ${props => props.theme.colors.primary.main};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  transition: ${props => props.theme.transitions.default};
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
  }
  
  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.text.muted};
  line-height: 1.7;
`;

const WhyBlueSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Crown,
      title: t('why.point1.title'),
      description: t('why.point1.desc'),
    },
    {
      icon: Palette,
      title: t('why.point2.title'),
      description: t('why.point2.desc'),
    },
    {
      icon: Heart,
      title: t('why.point3.title'),
      description: t('why.point3.desc'),
    },
  ];

  return (
    <Section id="why-blue">
      <DecorativeBackground />
      <Container>
        <ContentWrapper>
          <SectionHeader>
            <Subtitle>{t('why.subtitle')}</Subtitle>
            <Title>{t('why.title')}</Title>
            <Description>{t('why.description')}</Description>
          </SectionHeader>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <IconWrapper>
                  <feature.icon />
                </IconWrapper>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default WhyBlueSection;
